/**
 * "Request Bank Account" — pick which household members should connect
 * another bank account via Flinks, then send by email or message (client
 * 2026-08-27; it used to be a single-applicant confirm). Posts
 * `{ intent: 'requestBankAccount', applicationIds, channel }`, which the
 * server loops: dedicated "Additional Bank Details Needed" email per target,
 * verify-income step reset, application → in_progress.
 *
 * Shares ApplicantPicker with Request Documents, so the same dialog works on
 * the individual page (household of one) and the combined page.
 */
import { Mail, Send } from 'lucide-react'
import { useState } from 'react'
import { useFetcher } from 'react-router'
import type {
	ApplicationSummary,
	DetailActionResult,
	GroupMember,
} from '#app/utils/application-detail-v2/types'
import { ApplicantPicker, buildApplicantOptions } from './ApplicantPicker'
import {
	ActionResultMarker,
	DetailDialog,
	InlineActionError,
	PillButton,
} from './dialog-bits'

export function RequestBankDialog({
	open,
	onOpenChange,
	application,
	groupMembers = [],
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
	/** Household members; empty (solo) falls back to the page's applicant. */
	groupMembers?: GroupMember[]
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const [selectedIds, setSelectedIds] = useState<string[]>([application.id])
	const busy = fetcher.state !== 'idle'

	const handleOpenChange = (next: boolean) => {
		if (!next) setSelectedIds([application.id])
		onOpenChange(next)
	}

	const options = buildApplicantOptions(application, groupMembers)
	const toggle = (id: string) =>
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		)

	const selectedPhones = options
		.filter((option) => selectedIds.includes(option.id) && option.phone)
		.map((option) => option.phone as string)

	const submit = (channel: 'email' | 'message') => {
		void fetcher.submit(
			{
				intent: 'requestBankAccount',
				applicationIds: selectedIds.join(','),
				channel,
			},
			{ method: 'post' },
		)
		if (channel === 'message' && selectedPhones.length > 0) {
			// Device SMS composer, like Reach Out — the recorded request is the
			// source of truth either way.
			const body = encodeURIComponent(
				'Hi, we need an additional bank account connected for your rental application. Please open your application to add one.',
			)
			window.location.href = `sms:${selectedPhones.join(',')}?&body=${body}`
		}
	}

	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback="Bank account request sent successfully"
				onSuccess={() => handleOpenChange(false)}
			/>
			<DetailDialog
				open={open}
				onOpenChange={handleOpenChange}
				title="Request Bank Account"
				description="Select which applicants you would like to request an additional bank account."
				className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-2xl"
			>
				<ApplicantPicker
					className="mt-4"
					options={options}
					selectedIds={selectedIds}
					onToggle={toggle}
					disabled={busy}
				/>
				<InlineActionError fetcher={fetcher} open={open} />
				<div className="mt-6 flex flex-wrap items-center justify-end gap-3">
					<PillButton
						tone="outline"
						disabled={busy || selectedIds.length === 0}
						onClick={() => submit('email')}
					>
						Send Email <Mail className="size-4" />
					</PillButton>
					<PillButton
						tone="dark"
						disabled={
							busy || selectedIds.length === 0 || selectedPhones.length === 0
						}
						title={
							selectedPhones.length === 0
								? 'No phone number on file for the selected applicants'
								: undefined
						}
						onClick={() => submit('message')}
					>
						Send Message <Send className="size-4" />
					</PillButton>
				</div>
			</DetailDialog>
		</>
	)
}
